import {fromJS, Map, OrderedSet, Set} from "immutable";
import {LOGIN, LOGOUT} from "../actions/AuthActions";
import {getWarehouseLabelFromId} from "../lib/WarehouseHelper";

const defaultProfile = fromJS({
  email: ""
})

const initialState = fromJS({
  authenticated: false,
  profile: defaultProfile,
  scopes: Set(),
  userMetadata: Map(),
  isWarehouseOperator: false,
  accessibleWarehouseIds: Set(),
})

export default function(state = initialState, action) {
  switch(action.type) {
    case LOGIN.SUCCESS:
      const updatedState = state.set("authenticated", true)
        .set("scopes", Set(action.scopes))
        .set("userMetadata", Map(action.userMetadata));
      
      const hack_isWarehouseOperator = action.userMetadata.hack === "warehouse_operator";

      return hack_isWarehouseOperator
        ? updatedState
            .set("isWarehouseOperator", true)
            .set("accessibleWarehouseIds", Set(action.scopes.filter((s) => s.startsWith("warehouse:") || s.startsWith("tracking:")).map((s) => s.split(":")[1])))
        : updatedState;

    case LOGIN.FAILED:
    case LOGOUT.SUCCESS:
      return state.set("authenticated", false)
                  .set("profile", defaultProfile);

    default:
      return state;

  }
}

export function canReadShipping(state) {
  return !isWarehouseOperator(state) && state.get("scopes").has("read:shipping");
}

export function canReadSales(state) {
  return !isWarehouseOperator(state) && state.get("scopes").has("read:sales");
}

export function canReadNotes(state) {
  return !isWarehouseOperator(state);
}

export function canReadInventory(state) {
  return !isWarehouseOperator(state) && (state.get("scopes").has("read:inventory") || state.get("scopes").has("read:production"));
}

export function canReadAdmin(state) {
  return !isWarehouseOperator(state) && state.get("scopes").has("read:admin");
}

export function getAccessibleWarehouseIds(state) {
  return getAccessibleIds(state, isWarehouseScope);
}

function getAccessibleIds(state, isGoodScope = (x) => false) {
  return state.get("scopes")
    .filter(isGoodScope)
    .map((w) => w.split(":")[1]);
}

function isStatisticsScope(scope) {
  return scope && scope.startsWith("statistics");
}
function isWarehouseScope(scope) {
  return scope?.startsWith("warehouse");
}
function isTrackingScope(scope) {
  return scope?.startsWith("tracking");
}

function isWarehouseOperator(state) {
  return state.get("isWarehouseOperator");
}

// Dashboards depend on the rights listed in "scopes"
export function getDashboards(state) {
  const dashboards = [];

  if (isWarehouseOperator(state)) {
    // Warehouse operators have access to X warehouses
    const warehouseIds = getAccessibleIds(state, isWarehouseScope)

    // And optionally to the consignments tracking of Y warehouses (usually X=Y)
    const warehouseIdsForTracking = getAccessibleIds(state, isTrackingScope)

    // We want one menu per warehouse id (either warehouse or tracking)
    const allUniqueWarehouseIds = state.get("accessibleWarehouseIds");

    allUniqueWarehouseIds.forEach((warehouseId) => {
      const dashboardsForWarehouse = [];

      // Warehouses (Kanban / Packign process flow)
      if (warehouseIds.includes(warehouseId)) {
        dashboardsForWarehouse.push({
          to: "/?warehouse=" + warehouseId,
          label: "Warehouse Packing Process",
        });

        dashboardsForWarehouse.push({
          to: "/inventory/jobs/" + warehouseId + "?active=true",
          label: "Warehouse Jobs",
        });
      }

      // Consignment Tracking for warehouses
      if (warehouseIdsForTracking.includes(warehouseId)) {
        dashboardsForWarehouse.push({
          to: "/?tracking=" + warehouseId,
          label: "Courier Tracking",
        });
      }

      // Stock Control page restricted to the warehouse ids the user has access to
      if (warehouseIds.includes(warehouseId)) {
        dashboardsForWarehouse.push({
          to: `/stock?warehouse_id=${warehouseId}`,
          label: "Stock control",
        });
      }

      dashboards.push({
        title: getWarehouseLabelFromId(warehouseId),
        links: dashboardsForWarehouse,
      });
    });
    
    return dashboards
  }

  // Pickup and/or sales orders
  const canSeeShipping = canReadShipping(state);
  const canSeeSales = canReadSales(state);
  if (canSeeShipping || canSeeSales) {
    let label = "Pickups and Sales Orders";
    if (!canSeeShipping) {
      label = "Sales Orders"
    } else if (!canSeeSales) {
      label = "Shipment Pickups"
    }
    dashboards.push({ to: "/?orders", label: label })
  }

  // Warehouses
  const warehousesLinks = getAccessibleIds(state, isWarehouseScope).map((warehouseId) => (
    { to: "/?warehouse=" + warehouseId, label: "Packing Process for " + getWarehouseLabelFromId(warehouseId) }
  ));
  dashboards.push(...warehousesLinks.toJS());

  // Tracking for warehouses
  const warehousesTrackingLinks = getAccessibleIds(state, isTrackingScope).map((warehouseId) => (
    { to: "/?tracking=" + warehouseId, label: "Courier Tracking for " + getWarehouseLabelFromId(warehouseId) }
  ));
  dashboards.push(...warehousesTrackingLinks.toJS());

  // Statistics
  const statisticsLinks = getAccessibleIds(state, isStatisticsScope).map((statisticsId) => (
    { to: "/?statistics=" + statisticsId, label: "Statistics" }
  ));
  dashboards.push(...statisticsLinks.toJS());

  return ([{
    title: "Dashboards",
    links: dashboards,
  }]);
}

export function defaultDashboard(state) {
  const defaultDashboardId = state.getIn(["userMetadata", "default_dashboard"]);

  // Make sure there is no discrepancy in the auth0 config and that this
  // dashboard is within the client's scope
  if (defaultDashboardId && state.get("scopes").includes(defaultDashboardId)) {
    return defaultDashboardId;
  }

  return "";
}

// read/write production/admin/inventory/sales/shipping